import React, { useState, useEffect, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import UseAlert from '../../components/common/useAlerte'
import { addPlan } from '../../redux/plans/planActions'
import { getMissions } from '../../redux/missions/missionActions'
import SelectPriority from '../../components/common/SelectPriority'
import SelectobjMaturite from '../../components/common/SelectObjM'
import SelectMission from '../../components/common/SelectMission'
import QuickCreate, { QuickCreated, useQuickCreate } from '../../components/quickCreate/QuickCreate'
import { QuickEntity } from '../../components/quickCreate/quickCreateErrors'
import DateSelector from '../../components/common/Date'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import SelectStatut from '../../components/common/SelectStatut'
import { resetFields } from '../../utils/emptyFormFields'
import { createSanitizedObjectHandler } from '../../utils/inputHandlers'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

const PlanCreate = () => {
  const { idmission } = useParams<{ idmission: string }>()
  const [currentPageSize, setCurrentPageSize] = useState<number>(5)
  const { t } = useTranslation()

  const [priority, setPriority] = useState<number>(3)
  const [status, setStatus] = useState<string>('A faire')
  const [ObjectifMaturite, setObjectifMaturite] = useState<string>('1')
  const [MissionId, setMissionId] = useState<string>(idmission || '')
  const handleDateChange = (dates: [Date, Date] | null) => {
    if (dates) {
      const [startDate, endDate] = dates

      setPlan((prevPlan) => ({
        ...prevPlan,
        DateDebut: startDate,
        DateFin: endDate,
      }))
    }
  }

  type SingleState = string | number | string[]

  const createHandler =
    <T extends SingleState>(
      setState: React.Dispatch<React.SetStateAction<T>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: T }>) => {
      const { value } = event.target

      setState(value)

      setPlan((prevPlan) => ({
        ...prevPlan,
        [property]: value,
      }))
    }

  const handleMissionChange = (newMission: string) => {
    setMissionId(newMission)
    setPlan((prevPlan) => ({
      ...prevPlan,
      MissionId: newMission,
    }))
  }

  const handleObjectifMaturiteChange = createHandler(
    setObjectifMaturite,
    'ObjectifMaturite'
  ) as (
    event: ChangeEvent<{ value: string }> | SelectChangeEvent<string>
  ) => void

  const handleStatusChange = createHandler(setStatus, 'Status') as (
    event: ChangeEvent<{ value: string }> | SelectChangeEvent<string>
  ) => void

  const handlePriorityChange = createHandler(setPriority, 'Priority') as (
    event: ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  useEffect(() => {
    dispatch(getMissions({ page: 1, pageSize: currentPageSize }))
  }, [currentPageSize])

  const [plan, setPlan] = useState({
    PlanName: '',
    Description: '',
    MissionId: idmission || '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: 'A faire',
    Priority: 3,
    ObjectifMaturite: '1',
  })

  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    status: 0,
  })
  const status1 = useSelector(
    (state: RootState) => state.plan.addedPlan?.status
  )
  const message = useSelector(
    (state: RootState) => state.plan.addedPlan?.message
  )
  const qc = useQuickCreate()
  const handleQuickCreated = (kind: QuickEntity, created: QuickCreated) => {
    qc.close()
    qc.bump(kind)
    if (kind === 'mission' && created._id) {
      const id = created._id as string
      setMissionId(id)
      setPlan((prevPlan) => ({ ...prevPlan, MissionId: id }))
    }
  }

  const handleInputChange = createSanitizedObjectHandler(setPlan, {
    PlanName: 'text',
    Description: 'text',
  })
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })

    if (alert.status === 200) {
      navigate('/pipelines')
    }
  }

  const submitLabel = t('buttons.add')

  const handleAddPlan = async () => {
    const {
      PlanName,
      Description,
      DateDebut,
      DateFin,
      Status,
      Priority,
      ObjectifMaturite,
    } = plan

    if (
      PlanName &&
      Description &&
      DateDebut &&
      DateFin &&
      Status &&
      Priority &&
      ObjectifMaturite &&
      MissionId
    ) {
      try {
        await dispatch(addPlan(plan))
        setAlert({
          open: true,
          message: message || 'Plan added successfully.',
          status: status1 || 200,
        })

        resetPlan()
      } catch (error) {
        setAlert({
          open: true,
          message: 'An error occurred while adding the plan. Please try again.',
          status: 500,
        })
      }
    } else {
      setAlert({
        open: true,
        message: 'Please fill out all fields.',
        status: 0,
      })
    }
  }

  const resetPlan = () => {
    setPlan({
      PlanName: '',
      Description: '',
      MissionId: idmission || '',
      DateDebut: new Date(),
      DateFin: new Date(),
      Status: 'A faire',
      Priority: 3,
      ObjectifMaturite: '1',
    })
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.plan.createEyebrow', {
          defaultValue: 'Management · Risk treatment plans',
        })}
        title={t('buttons.addPlan', { defaultValue: 'New risk treatment plan' })}
        subtitle={t('entityForms.plan.createSubtitle', {
          defaultValue:
            'Define the plan name, mission, schedule and maturity objective.',
        })}
        backLabel={t('entityForms.plan.backToList', {
          defaultValue: 'Back to risk treatment plans',
        })}
        onBack={() => navigate('/pipelines')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.plan.identityTitle', {
                defaultValue: 'Plan details',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('plan_name')} required>
                  <input
                    className="incyia-form-inp"
                    name="PlanName"
                    value={plan.PlanName}
                    onChange={handleInputChange as any}
                    required
                  />
                </FormField>
                <FormField label={t('Mission')} required>
                  <SelectMission
                    value={plan.MissionId}
                    onChange={handleMissionChange}
                    onCreateNew={qc.open('mission')}
                    reloadKey={qc.keyOf('mission')}
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={plan.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.plan.planningTitle', {
                defaultValue: 'Planning and objectives',
              })}
            >
              <FormGrid cols={2}>
                <FormField
                  label={t('entityForms.project.dates', {
                    defaultValue: 'Start & end dates',
                  })}
                  span
                >
                  <DateSelector
                    placeholder={t('entityForms.datePlaceholder')}
                    onDateChange={handleDateChange}
                  />
                </FormField>
                <FormField label={t('Maturity objective')}>
                  <SelectobjMaturite
                    value={plan.ObjectifMaturite}
                    onChange={handleObjectifMaturiteChange}
                  />
                </FormField>
                <FormField label={t('Status')}>
                  <SelectStatut value={status} onChange={handleStatusChange} />
                </FormField>
                <FormField label={t('Priority')}>
                  <SelectPriority
                    value={plan.Priority}
                    onChange={handlePriorityChange}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('buttons.cancel', { defaultValue: 'Cancel' })}
              submitLabel={submitLabel}
              onCancel={() => navigate('/pipelines')}
              onSubmit={handleAddPlan}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.plan.tip', {
              defaultValue:
                'Plans group risks and treatment actions under a shared maturity target.',
            })}
            steps={[
              {
                title: t('entityForms.plan.identityTitle', {
                  defaultValue: 'Plan details',
                }),
                fieldCount: 3,
              },
              {
                title: t('entityForms.plan.planningTitle', {
                  defaultValue: 'Planning and objectives',
                }),
                fieldCount: 4,
              },
            ]}
            requiredCount={3}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
      <QuickCreate request={qc.request} onClose={qc.close} onCreated={handleQuickCreated} />
    </div>
  )
}

export default PlanCreate
